import { useEffect, useState } from 'react';
import { useVehicleQuery } from 'SharedHooks/useVehicleQuery';
import { useDataFetch } from 'SharedHooks/useDataFetch';
import { useForm } from 'SharedHooks/useForm';

export const pushTrackingToDataLayer = (trackingData, regPlate, regPlateValid, make, model) => {
  if (trackingData && Object.keys(trackingData).length > 0) {
    const updatedTrackingData = { ...trackingData };
    Object.keys(updatedTrackingData).forEach((key) => {
      if (typeof updatedTrackingData[key] === 'string') {
        updatedTrackingData[key] = updatedTrackingData[key]
          .replace('[vrm]', regPlate)
          .replace('[vrm-valid]', regPlateValid)
          .replace('[make]', make || '')
          .replace('[model]', model || '');
      }
    });

    window.dataLayer?.push(updatedTrackingData);
  }
};

const UTMParameters = ['utm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_source_platform', 'utm_term', 'utm_content', 'utm_account'];
const checkUTMParameters = (urlSearchParams) => UTMParameters.some(value => urlSearchParams.get(value));
const checkAndAddURLSearchParams = (url) => {
  const urlSearchParams = new URLSearchParams(window.location?.search);

  if (checkUTMParameters(urlSearchParams)) {
    return url + (url.includes('?') ? '&' : '?') + urlSearchParams.toString();
  }
  return url;
};

export const updateURL = (regPlate, baseUrl, passParams) => {
  // Replace the [vrm] placeholder in the URL with the VRM.
  let url = baseUrl.replace('[vrm]', `${regPlate}`);

  // Pass search parameters from the current window location to the URL if passParams is true
  if (passParams) {
    url = checkAndAddURLSearchParams(url);
  }

  return url;
};

export const openURL = (urlToOpen, submitTypeIsButton) => {
  if (submitTypeIsButton) {
    // Where submit type is button, this should open in the same tab
    if (window !== undefined) window.open(urlToOpen, '_self');
  } else {
    // Where submit type is NOT button, this should open in a new tab if possible
    // eslint-disable-next-line no-lonely-if
    if (typeof window !== 'undefined' && window.open) {
      // eslint-disable-next-line no-unused-expressions
      window.open(urlToOpen, '_blank') || window.open(urlToOpen, '_self');
    }
  }
};

export const useRegPlateForm = ({
  ctaUrl = '',
  isRequired = true,
  passParams = false,
  queryType = 'ukv',
  submitAction = null,
  submitTypeIsButton = false,
  trackingData = {},
  vrm = null,
}) => {
  const {
    query,
    getData,
    getHasError,
    getVrm,
    getAdditionalQueryVariables,
  } = useVehicleQuery(queryType);

  const vrmToUse = vrm || (typeof window !== 'undefined' && window.sessionStorage && window.sessionStorage.getItem('vrm')) || '';

  const { state: vehicleDataFetchState, fetchData } = useDataFetch({ query });
  const [unvalidatedVRM, setUnvalidatedVRM] = useState();
  const [error, setError] = useState('');

  const {
    formData,
    handleInputChange,
    handleSubmit,
  } = useForm({
    vehicleReg: vrmToUse,
  }, (data) => {
    if (data.vehicleReg) {
      setError('');
      const formattedReg = data.vehicleReg.replace(/ /g, '').toUpperCase();
      if (formattedReg) {
        setUnvalidatedVRM(formattedReg);
        const variables = { vrm: formattedReg, ...getAdditionalQueryVariables() };
        fetchData({ variables });
      }
    } else {
      // eslint-disable-next-line no-unused-expressions
      isRequired ? setError('Required Vehicle Registration Number') : window.open(ctaUrl, '_blank');
    }
  });

  useEffect(() => {
    if (vehicleDataFetchState.isError) {
      setError('There is a problem with the GraphQL request.');
      return;
    }

    if (vehicleDataFetchState.isSuccess) {
      const regPlateValid = !getHasError(vehicleDataFetchState);

      if (vehicleDataFetchState.data && getData(vehicleDataFetchState) && !getHasError(vehicleDataFetchState)) {
        const regPlate = getVrm(vehicleDataFetchState);

        // If there is an action to perform on submit, do that now.
        if (submitAction !== null) {
          submitAction(getData(vehicleDataFetchState));
        }

        // Tracking
        pushTrackingToDataLayer(
          trackingData,
          regPlate,
          regPlateValid,
          getData(vehicleDataFetchState)?.make || undefined,
          getData(vehicleDataFetchState)?.model || undefined,
        );

        // Update URL with regPlate and params
        const url = updateURL(regPlate, ctaUrl, passParams);

        // Open updated url
        openURL(url, submitTypeIsButton);
      } else {
        setError('Please enter a valid Vehicle Registration Number');
        pushTrackingToDataLayer(trackingData, unvalidatedVRM, regPlateValid);
      }
    }
  }, [
    vehicleDataFetchState.isError,
    vehicleDataFetchState.isSuccess,
    vehicleDataFetchState.data,
    getHasError,
    getData,
    getVrm,
    setError,
    unvalidatedVRM,
    trackingData,
  ]);

  return {
    formData,
    handleInputChange,
    handleSubmit,
    error,
  };
};
